import { useMemo } from "react";
import { MailOpen, UserCheck, UserRound, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const first = (n) => (n || "").split(" ")[0];

// Mine / All / Unassigned + a chip per colleague the visible emails are assigned to — and an Unread toggle on the right.
export function AssigneeFilter({ emails, me, value, onChange, unreadOnly = false, onUnreadToggle }) {
  const myName = (me?.name || "").toLowerCase();
  const myEmail = (me?.email || "").toLowerCase();
  const isMine = (e) => e.assigned && ((e.assigned.email && e.assigned.email.toLowerCase() === myEmail) || (e.assigned.name || "").toLowerCase() === myName);

  const chips = useMemo(() => {
    const mine = emails.filter(isMine).length;
    const unassigned = emails.filter(e => !e.assigned).length;
    const others = {};
    emails.forEach(e => {
      if (e.assigned && !isMine(e)) {
        const k = e.assigned.name || e.assigned.email || "?";
        others[k] = (others[k] || 0) + 1;
      }
    });
    return [
      { id: "all", label: "All", count: emails.length, icon: Users },
      { id: "mine", label: "Mine", count: mine, icon: UserCheck },
      { id: "unassigned", label: "Unassigned", count: unassigned, icon: UserRound },
      ...Object.entries(others).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ id: `who:${name}`, label: first(name), title: name, count })),
    ];
  }, [emails]); // eslint-disable-line react-hooks/exhaustive-deps

  const unread = emails.filter(e => !e.is_read).length;
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-white overflow-x-auto shrink-0" data-testid="assignee-filter">
      {chips.map(c => (
        <button key={c.id} type="button" onClick={() => onChange(c.id)} title={c.title || c.label}
          className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] whitespace-nowrap transition-colors",
            value === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}
          data-testid={`assignee-filter-${c.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          {c.icon && <c.icon className="w-2.5 h-2.5" />}{c.label}
          <span className={cn("tabular-nums", value === c.id ? "text-slate-300" : "text-slate-400")}>{c.count}</span>
        </button>
      ))}
      {onUnreadToggle && (
        <button type="button" onClick={() => onUnreadToggle(!unreadOnly)} title="Show only unread emails"
          className={cn("ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] whitespace-nowrap transition-colors",
            unreadOnly ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}
          data-testid="unread-filter-toggle">
          <MailOpen className="w-2.5 h-2.5" />Unread
          <span className={cn("tabular-nums", unreadOnly ? "text-blue-100" : "text-slate-400")}>{unread}</span>
        </button>
      )}
    </div>
  );
}

export function applyAssigneeFilter(emails, filter, me, unreadOnly = false) {
  const base = unreadOnly ? emails.filter(e => !e.is_read) : emails;
  if (!filter || filter === "all") return base;
  const myName = (me?.name || "").toLowerCase();
  const myEmail = (me?.email || "").toLowerCase();
  const isMine = (e) => e.assigned && ((e.assigned.email && e.assigned.email.toLowerCase() === myEmail) || (e.assigned.name || "").toLowerCase() === myName);
  if (filter === "mine") return base.filter(isMine);
  if (filter === "unassigned") return base.filter(e => !e.assigned);
  const who = filter.replace(/^who:/, "");
  return base.filter(e => e.assigned && (e.assigned.name || e.assigned.email) === who);
}
